import React, {
  useState, useEffect, useMemo, memo, useCallback,
} from 'react'

import {
  classnames, ClosingDropdown, ChevronDown, ChevronUp, Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, node, number, oneOf, string, func, bool } from 'prop-types'

import { EMPTY_ARRAY, NOOP } from 'Utils/stableDefaults'
import useBodyScrollLock from 'Utils/useBodyScrollLock'
import useDelayLoad from 'Utils/useDelayLoad'

function FiltersWrapper({
  placeholder,
  reservedPlaceholder = '',
  positionHorizontal = 'right',
  content,
  onOpenChange = null,
  triggerCloseDropdown = false,
  setTriggerCloseDropdown = NOOP,
  prefixIcon = null,
  hasSelectedAnyFilters = false,
  selectedValues = EMPTY_ARRAY,
  selectedCount = 0,
  reservedCount = 0,
  reservedValue = '',
  closingDropdownClassName = '',
  contentClassName = '',
  size = 'large',
  trailIconSize = 'default',
}) {
  const { isLoaded, elementRef } = useDelayLoad()
  const [isOpen, setIsOpen] = useState(false)
  // Triangle's ClosingDropdown only closes on an outside mousedown, so Escape and a responsive
  // hide left the panel and its backdrop up. triggerCloseDropdown is its supported external close
  // channel; keep a local flag so this works whether or not the parent also drives that prop.
  const [forceClose, setForceClose] = useState(false)

  // The panel is absolutely positioned against a trigger in normal flow, so page scrolling used
  // to carry it off-screen while it stayed open behind the full-viewport backdrop, which then
  // swallowed the next click anywhere on the page.
  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen)
    }
  }, [isOpen, onOpenChange])

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        // Closing unmounts the panel, which would drop keyboard focus to <body>.
        const dropdown = elementRef.current?.closest('.cgt-dropdown')
        if (dropdown?.contains(document.activeElement)) {
          elementRef.current.focus()
        }
        setForceClose(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, elementRef])

  // Responsive slots hide their filter with `display: none` while React keeps it mounted, so an
  // open dropdown would keep the body scroll lock on with no visible panel or backdrop to dismiss.
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const closeWhenHidden = () => {
      // offsetParent is null once the element or one of its ancestors is display: none.
      if (elementRef.current && elementRef.current.offsetParent === null) {
        setForceClose(true)
      }
    }

    window.addEventListener('resize', closeWhenHidden)

    return () => window.removeEventListener('resize', closeWhenHidden)
  }, [isOpen, elementRef])

  const position = useMemo(() => ({ horizontal: positionHorizontal }), [positionHorizontal])

  const handleOpenChange = useCallback((open) => {
    setIsOpen(open)
  }, [])

  // Triangle's trigger wrapper is a div that only listens for clicks; re-dispatching a click lets
  // it bubble into that toggle so keyboard users can open and close the panel.
  const handleTriggerKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      event.currentTarget.click()
    }
  }, [])

  // ClosingDropdown resets the trigger to false once it has closed; clear both sources.
  const handleTriggerCloseChange = useCallback((value) => {
    setForceClose(value)
    setTriggerCloseDropdown(value)
  }, [setTriggerCloseDropdown])

  const hasSelectedValues = selectedValues.length > 0
  // One value reads "Licenses: Royalty Free", two or more only the count ("Licenses +2"), so a label
  // never outgrows the trigger's longest-value width.
  const showsValue = selectedValues.length === 1
  // selectedCount is for triggers that only count what is on inside them (More filters).
  const moreSelectedCount = hasSelectedValues && !showsValue ? selectedValues.length : selectedCount
  const isActive = hasSelectedValues || selectedCount > 0

  const className = useMemo(() => classnames(
    "cgt-text-input cgt-text-input--medium cgt-text-input--primary cgt-text-input--icon-trail filter-wrapper__trigger",
    hasSelectedAnyFilters && 'filter-wrapper__trigger--filled',
    isActive && 'filter-wrapper__trigger--active',
    isOpen && 'filter-wrapper__trigger--open',
  ), [hasSelectedAnyFilters, isActive, isOpen])

  const label = isActive ? (
    <div className="filter-wrapper__placeholder filter-wrapper__placeholder--active">
      <span>
        {placeholder}
        {showsValue && (
          <span className="filter-wrapper__selection">
            {': '}
            <span className="filter-wrapper__selected-value">{selectedValues[0]}</span>
          </span>
        )}
      </span>
      {/* Nested, not a trigger child: Triangle's `.cgt-text-input > *` font-size ties the caption class. */}
      {moreSelectedCount > 0 && (
        <span className="filter-wrapper__more-count cgt-caption-semibold cgt-caption-semibold--dark-bg">
          {`+${moreSelectedCount}`}
        </span>
      )}
    </div>
  ) : (
    <div className="filter-wrapper__placeholder">
      {placeholder}
      {reservedPlaceholder && (
        <span className="filter-wrapper__placeholder-reserve" aria-hidden="true">
          {reservedPlaceholder}
        </span>
      )}
    </div>
  )

  // An invisible ghost of the widest label shares one grid cell with the visible one, so the trigger
  // keeps that width whatever is selected and only grows for a label that is longer still:
  // "Title: <longest value>" for value triggers, "Title +<max>" with the real badge for count-only
  // ones (the content-sized secondary row would otherwise shove its siblings on select).
  let reserve = null
  if (reservedValue) {
    reserve = (
      <span className="filter-wrapper__fit-reserve" aria-hidden="true">
        {`${placeholder}: `}
        <span className="filter-wrapper__selected-value">{reservedValue}</span>
      </span>
    )
  } else if (reservedCount > 0) {
    reserve = (
      <div
        className="filter-wrapper__placeholder filter-wrapper__placeholder--active filter-wrapper__fit-reserve"
        aria-hidden="true"
      >
        <span>{placeholder}</span>
        <span className="filter-wrapper__more-count cgt-caption-semibold cgt-caption-semibold--dark-bg">
          {`+${reservedCount}`}
        </span>
      </div>
    )
  }

  return (
    <ClosingDropdown
      // '' avoids null so the required `content` propType is satisfied; falsy still renders nothing
      content={isLoaded ? content : ''}
      isSimpleContent
      isSpaced
      size={size}
      position={position}
      contentClassName={contentClassName}
      onOpenChange={handleOpenChange}
      triggerCloseDropdown={triggerCloseDropdown || forceClose}
      setTriggerCloseDropdown={handleTriggerCloseChange}
      className={closingDropdownClassName}
      withBackdrop
    >
      <div
        ref={elementRef}
        className={className}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onKeyDown={handleTriggerKeyDown}
      >
        {prefixIcon}
        {reserve ? (
          <div className="filter-wrapper__fit">
            {reserve}
            {label}
          </div>
        ) : label}
        <Icon
          svgComponent={isOpen ? ChevronUp : ChevronDown}
          color="darker"
          size={trailIconSize}
          notSpaced
          className="filter-wrapper__trail-icon"
        />
      </div>
    </ClosingDropdown>
  )
}

FiltersWrapper.propTypes = {
  placeholder: string.isRequired,
  reservedPlaceholder: string,
  positionHorizontal: oneOf(['left', 'right']),
  content: node.isRequired,
  onOpenChange: func,
  triggerCloseDropdown: bool,
  setTriggerCloseDropdown: func,
  prefixIcon: node,
  hasSelectedAnyFilters: bool,
  selectedValues: arrayOf(string),
  selectedCount: number,
  reservedCount: number,
  reservedValue: string,
  closingDropdownClassName: string,
  contentClassName: string,
  size: oneOf(['large', 'medium']),
  trailIconSize: oneOf(['default', 'small']),
}

export default memo(FiltersWrapper)
